import { useEffect, useRef, useState } from "react";
import {
  X, Sparkles, Plus, Trash2, Upload, Link2,
  FileText, Star, Loader2, Info,
} from "lucide-react";
import {
  useFreelancerJobRecordsStore,
  type JobRecord,
  type JobConversation,
  type WorkSample,
  type WorkSampleType,
  type ExtractionResult,
} from "../../store/useFreelancerJobRecordsStore";
import ExtractionReview from "./ExtractionReview";

/**
 * Add / edit one job record.
 *
 * Tabbed rather than one long form: a complete record has six distinct kinds of
 * information, and stacking them vertically makes the ones at the bottom — work
 * samples and client feedback, the parts that turn a record into usable evidence
 * — the ones people stop filling in.
 *
 * The AI tab is read-only by design. Everything on it is derived from the other
 * five, and letting someone edit it would turn an inference into a claim nobody
 * can trace.
 */

const TABS = [
  { key: "job", label: "Job & Contract" },
  { key: "proposal", label: "Proposal" },
  { key: "delivered", label: "Delivered & Skills" },
  { key: "feedback", label: "Client Feedback" },
  { key: "samples", label: "Work Samples" },
  { key: "conversation", label: "Client Conversation" },
  { key: "ai", label: "AI Insights" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const SAMPLE_TYPES: WorkSampleType[] = ["Image", "Portfolio URL", "Live Website", "Case Study", "File"];

const input =
  "w-full px-3 py-2 bg-surface-container-low border border-outline-variant rounded-lg text-sm text-on-surface outline-none focus:ring-2 focus:ring-primary";

const Label = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <span className="block text-[11px] font-bold uppercase tracking-wider text-on-surface-variant mb-1">
    {children}
    {required && <span className="text-error ml-0.5">*</span>}
  </span>
);

/** Comma-separated tags, kept as a string while typing so commas are usable. */
const TagInput = ({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
}) => {
  const [draft, setDraft] = useState(value.join(", "));
  useEffect(() => setDraft(value.join(", ")), [value.join("|")]);
  return (
    <>
      <input
        className={input}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onChange(draft.split(",").map((s) => s.trim()).filter(Boolean))}
      />
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {value.map((t) => (
            <span key={t} className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-[11px] font-semibold">
              {t}
            </span>
          ))}
        </div>
      )}
    </>
  );
};

/** One extracted bucket. Hidden entirely when empty — an empty heading reads as
 *  a failure rather than as "there was nothing of this kind in the thread". */
const SignalList = ({
  title,
  items,
  hint,
  chips = false,
}: {
  title: string;
  items: string[];
  hint?: string;
  chips?: boolean;
}) => {
  if (!items?.length) return null;
  return (
    <div>
      <Label>{title}</Label>
      {chips ? (
        <div className="flex flex-wrap gap-1">
          {items.map((t, i) => (
            <span key={i} className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[11px] font-semibold">
              {t}
            </span>
          ))}
        </div>
      ) : (
        <ul className="space-y-1">
          {items.map((t, i) => (
            <li key={i} className="text-sm text-on-surface leading-relaxed">· {t}</li>
          ))}
        </ul>
      )}
      {hint && <span className="block text-[11px] text-on-surface-variant mt-1">{hint}</span>}
    </div>
  );
};

const blank = () => ({
  jobTitle: "",
  jobDescription: "",
  category: "",
  subcategory: "",
  jobUrl: "",
  contract: {
    value: 0,
    currency: "USD",
    duration: "",
    startDate: "",
    endDate: "",
    type: "Fixed Price",
    status: "Completed",
  },
  proposal: { coverLetter: "", content: "", url: "", attachments: [] },
  delivered: { summary: "", items: [] as { title: string; detail: string }[] },
  skills: [] as string[],
  technologies: [] as string[],
  feedback: { rating: "", review: "", reviewDate: "", clientName: "" },
  workSamples: [] as WorkSample[],
  conversation: { transcript: "", source: "pasted" },
});

const dateValue = (d?: string | null) => (d ? String(d).slice(0, 10) : "");

const JobRecordModal = ({
  open,
  recordId,
  onClose,
}: {
  open: boolean;
  recordId: string | null;
  onClose: () => void;
}) => {
  const fetchRecord = useFreelancerJobRecordsStore((s) => s.fetchRecord);
  const saveRecord = useFreelancerJobRecordsStore((s) => s.saveRecord);
  const generateAi = useFreelancerJobRecordsStore((s) => s.generateAi);
  const extractConversation = useFreelancerJobRecordsStore((s) => s.extractConversation);
  const extractFields = useFreelancerJobRecordsStore((s) => s.extractFieldsFromConversation);
  const accountId = useFreelancerJobRecordsStore((s) => s.accountId);
  const uploadSample = useFreelancerJobRecordsStore((s) => s.uploadSample);

  const [tab, setTab] = useState<TabKey>("job");
  const [form, setForm] = useState<any>(blank());
  const [ai, setAi] = useState<JobRecord["ai"] | null>(null);
  const [conversation, setConversation] = useState<JobConversation | null>(null);
  const [analysing, setAnalysing] = useState(false);
  // The proposal from reading the conversation into fields. Held here, not
  // written into the form, until the BD applies it.
  const [extraction, setExtraction] = useState<ExtractionResult | null>(null);
  const [reading, setReading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setTab("job");
    if (!recordId) {
      setForm(blank());
      setAi(null);
      setConversation(null);
      return;
    }
    setLoading(true);
    fetchRecord(recordId).then((r) => {
      if (r) {
        setForm({
          jobTitle: r.jobTitle,
          jobDescription: r.jobDescription,
          category: r.category,
          subcategory: r.subcategory,
          jobUrl: r.jobUrl,
          contract: {
            ...r.contract,
            startDate: dateValue(r.contract.startDate),
            endDate: dateValue(r.contract.endDate),
          },
          proposal: r.proposal,
          delivered: {
            summary: r.delivered.summary,
            items: r.delivered.items.map((d) => ({ title: d.title, detail: d.detail })),
          },
          skills: r.skills,
          technologies: r.technologies,
          feedback: {
            ...r.feedback,
            rating: r.feedback.rating === null ? "" : String(r.feedback.rating),
            reviewDate: dateValue(r.feedback.reviewDate),
          },
          workSamples: r.workSamples,
          conversation: { transcript: r.conversation?.transcript || "", source: r.conversation?.source || "pasted" },
        });
        setAi(r.ai);
        setConversation(r.conversation || null);
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, recordId]);

  if (!open) return null;

  const set = (patch: any) => setForm((f: any) => ({ ...f, ...patch }));
  const setIn = (key: string, patch: any) => setForm((f: any) => ({ ...f, [key]: { ...f[key], ...patch } }));

  // ── Applying what the conversation gave us ────────────────────────────────
  //
  // Only the fields the BD ticked, and only into the form — the record is
  // still saved through the normal path with every existing validation.

  // Does the form already hold something here? Used to decide which proposed
  // values would overwrite the BD's own typing, so those start unticked.
  const fieldOccupied = (path: string) => {
    const v = path.split(".").reduce((o: any, k) => (o == null ? undefined : o[k]), form);
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "number") return v !== 0;
    return Boolean(String(v ?? "").trim());
  };

  const applyExtraction = (chosen: string[], withDeliverables: boolean) => {
    if (!extraction) return;
    setForm((f: any) => {
      const next = { ...f };
      for (const path of chosen) {
        const value = extraction.fields[path]?.value;
        if (value === undefined) continue;
        const parts = path.split(".");
        if (parts.length === 1) {
          next[parts[0]!] = value;
        } else {
          const [head, leaf] = parts as [string, string];
          next[head] = { ...next[head], [leaf]: value };
        }
      }
      if (withDeliverables && extraction.deliverables.length) {
        // Added to what is already there rather than replacing it — the BD may
        // have typed some in before reading the conversation.
        const have = new Set((next.delivered?.items || []).map((d: any) => String(d.title).toLowerCase()));
        const add = extraction.deliverables
          .filter((d) => !have.has(d.title.toLowerCase()))
          .map((d) => ({ title: d.title, detail: d.detail }));
        next.delivered = { ...next.delivered, items: [...(next.delivered?.items || []), ...add] };
      }
      return next;
    });
    setExtraction(null);
  };

  const readConversation = async () => {
    setReading(true);
    const res = await extractFields(form.conversation.transcript, accountId || undefined);
    setReading(false);
    if (res) setExtraction(res);
  };

  const save = async () => {
    if (!form.jobTitle.trim()) {
      setTab("job");
      return;
    }
    setSaving(true);
    const body = {
      ...form,
      feedback: {
        ...form.feedback,
        rating: form.feedback.rating === "" ? null : Number(form.feedback.rating),
      },
    };
    const saved = await saveRecord(body, recordId || undefined);
    setSaving(false);
    if (saved) onClose();
  };

  const runAi = async () => {
    if (!recordId) return;
    setGenerating(true);
    const result = await generateAi(recordId, true);
    setGenerating(false);
    if (result) setAi(result);
  };

  const onPickImage = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const sample = await uploadSample(String(reader.result), file.name, file.size);
      setUploading(false);
      if (sample) set({ workSamples: [...form.workSamples, sample] });
      if (fileRef.current) fileRef.current.value = "";
    };
    reader.onerror = () => setUploading(false);
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <div className="bg-surface-container-lowest w-full max-w-4xl rounded-xl shadow-2xl my-auto max-h-[94vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-outline-variant">
          <div>
            <h2 className="text-base font-bold text-on-surface">
              {recordId ? "Edit Job Record" : "Add Job Record"}
            </h2>
            <p className="text-xs text-on-surface-variant mt-0.5">
              A completed job, kept whole — so the AI can use it as evidence later
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-on-surface-variant hover:bg-surface-container-low">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-5 pt-3 border-b border-outline-variant overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              disabled={t.key === "ai" && !recordId}
              title={t.key === "ai" && !recordId ? "Save the record first" : ""}
              className={`px-3 py-2 text-xs font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors disabled:opacity-40 ${
                tab === t.key
                  ? "border-primary text-primary"
                  : "border-transparent text-on-surface-variant hover:text-on-surface"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="py-12 text-center text-sm text-on-surface-variant">Loading the record…</div>
          ) : (
            <>
              {/* ── Job & contract ── */}
              {tab === "job" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="sm:col-span-2">
                    <Label required>Job title</Label>
                    <input
                      className={input}
                      value={form.jobTitle}
                      onChange={(e) => set({ jobTitle: e.target.value })}
                      placeholder="Shopify store redesign and checkout optimization"
                    />
                  </label>

                  <label className="sm:col-span-2">
                    <Label>Original job description</Label>
                    <textarea
                      className={`${input} min-h-[120px] resize-y`}
                      value={form.jobDescription}
                      onChange={(e) => set({ jobDescription: e.target.value })}
                      placeholder="Paste the client's original posting, word for word"
                    />
                    {/* This is the single most useful field for matching, and the
                        one people are most tempted to summarise. */}
                    <span className="block text-[11px] text-on-surface-variant mt-1">
                      Paste it as the client wrote it — future job posts are written in the same
                      language, which is what makes this match.
                    </span>
                  </label>

                  <label>
                    <Label>Category</Label>
                    <input className={input} value={form.category} onChange={(e) => set({ category: e.target.value })} placeholder="Ecommerce Development" />
                  </label>
                  <label>
                    <Label>Subcategory</Label>
                    <input className={input} value={form.subcategory} onChange={(e) => set({ subcategory: e.target.value })} placeholder="Shopify" />
                  </label>

                  <label className="sm:col-span-2">
                    <Label>Job / contract URL</Label>
                    <input className={input} value={form.jobUrl} onChange={(e) => set({ jobUrl: e.target.value })} placeholder="https://www.upwork.com/..." />
                  </label>

                  <div className="sm:col-span-2 pt-2 border-t border-outline-variant">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-tertiary mb-3">Contract</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <label>
                        <Label>Value</Label>
                        <input type="number" min={0} className={input} value={form.contract.value} onChange={(e) => setIn("contract", { value: e.target.value })} />
                      </label>
                      <label>
                        <Label>Currency</Label>
                        <input className={input} value={form.contract.currency} onChange={(e) => setIn("contract", { currency: e.target.value })} />
                      </label>
                      <label>
                        <Label>Duration</Label>
                        <input className={input} value={form.contract.duration} onChange={(e) => setIn("contract", { duration: e.target.value })} placeholder="6 weeks" />
                      </label>
                      <label>
                        <Label>Type</Label>
                        <select className={input} value={form.contract.type} onChange={(e) => setIn("contract", { type: e.target.value })}>
                          <option>Fixed Price</option>
                          <option>Hourly</option>
                        </select>
                      </label>
                      <label>
                        <Label>Start date</Label>
                        <input type="date" className={input} value={form.contract.startDate} onChange={(e) => setIn("contract", { startDate: e.target.value })} />
                      </label>
                      <label>
                        <Label>End date</Label>
                        <input type="date" className={input} value={form.contract.endDate} onChange={(e) => setIn("contract", { endDate: e.target.value })} />
                      </label>
                      <label className="col-span-2">
                        <Label>Status</Label>
                        <select className={input} value={form.contract.status} onChange={(e) => setIn("contract", { status: e.target.value })}>
                          <option>Completed</option>
                          <option>Ended Early</option>
                          <option>Cancelled</option>
                          <option>In Progress</option>
                        </select>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Proposal ── */}
              {tab === "proposal" && (
                <div className="space-y-4">
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <Info size={15} className="text-primary mt-0.5 shrink-0" />
                    <p className="text-xs text-on-surface leading-relaxed">
                      Kept as historical evidence. The proposal that won this job is the best
                      starting point for the next one, and the AI can compare what was promised
                      against what was delivered and what the client said.
                    </p>
                  </div>
                  <label className="block">
                    <Label>Cover letter</Label>
                    <textarea className={`${input} min-h-[140px] resize-y`} value={form.proposal.coverLetter} onChange={(e) => setIn("proposal", { coverLetter: e.target.value })} placeholder="The cover letter sent with the bid" />
                  </label>
                  <label className="block">
                    <Label>Full proposal</Label>
                    <textarea className={`${input} min-h-[120px] resize-y`} value={form.proposal.content} onChange={(e) => setIn("proposal", { content: e.target.value })} />
                  </label>
                  <label className="block">
                    <Label>Proposal URL</Label>
                    <input className={input} value={form.proposal.url} onChange={(e) => setIn("proposal", { url: e.target.value })} />
                  </label>
                </div>
              )}

              {/* ── Delivered & skills ── */}
              {tab === "delivered" && (
                <div className="space-y-4">
                  <label className="block">
                    <Label>What we delivered</Label>
                    <textarea className={`${input} min-h-[110px] resize-y`} value={form.delivered.summary} onChange={(e) => setIn("delivered", { summary: e.target.value })} placeholder="Rebuilt the theme, reduced checkout from five steps to two…" />
                  </label>

                  <div>
                    <Label>Deliverables</Label>
                    <div className="space-y-2">
                      {form.delivered.items.map((d: any, i: number) => (
                        <div key={i} className="flex items-start gap-2">
                          <input
                            className={input}
                            value={d.title}
                            placeholder="Shopify theme customization"
                            onChange={(e) =>
                              setIn("delivered", {
                                items: form.delivered.items.map((x: any, j: number) => (j === i ? { ...x, title: e.target.value } : x)),
                              })
                            }
                          />
                          <button
                            onClick={() => setIn("delivered", { items: form.delivered.items.filter((_: any, j: number) => j !== i) })}
                            className="p-2 text-outline hover:text-error shrink-0"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => setIn("delivered", { items: [...form.delivered.items, { title: "", detail: "" }] })}
                        className="text-primary text-xs font-semibold hover:underline flex items-center gap-1"
                      >
                        <Plus size={13} /> Add deliverable
                      </button>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-outline-variant">
                    {/* The distinction that makes matching trustworthy. */}
                    <div className="flex items-start gap-2 mb-3">
                      <Info size={14} className="text-tertiary mt-0.5 shrink-0" />
                      <p className="text-[11px] text-on-surface-variant leading-relaxed">
                        These are <b className="text-on-surface">demonstrated</b> skills — backed by this
                        completed contract. They carry more weight in matching than the skills listed
                        on the account's profile, which are only claimed.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Skills used</Label>
                        <TagInput value={form.skills} onChange={(v) => set({ skills: v })} placeholder="Shopify, Liquid, UX" />
                      </div>
                      <div>
                        <Label>Technologies</Label>
                        <TagInput value={form.technologies} onChange={(v) => set({ technologies: v })} placeholder="JavaScript, CSS" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Feedback ── */}
              {tab === "feedback" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label>
                    <Label>Star rating</Label>
                    <input type="number" min={0} max={5} step={0.1} className={input} value={form.feedback.rating} onChange={(e) => setIn("feedback", { rating: e.target.value })} placeholder="5" />
                  </label>
                  <label>
                    <Label>Review date</Label>
                    <input type="date" className={input} value={form.feedback.reviewDate} onChange={(e) => setIn("feedback", { reviewDate: e.target.value })} />
                  </label>
                  <label className="sm:col-span-2">
                    <Label>Original review text</Label>
                    <textarea className={`${input} min-h-[110px] resize-y`} value={form.feedback.review} onChange={(e) => setIn("feedback", { review: e.target.value })} placeholder="Paste the client's review word for word" />
                  </label>
                  <label className="sm:col-span-2">
                    <Label>Client name</Label>
                    <input className={input} value={form.feedback.clientName} onChange={(e) => setIn("feedback", { clientName: e.target.value })} placeholder="Where you're permitted to record it" />
                  </label>
                </div>
              )}

              {/* ── Work samples ── */}
              {tab === "samples" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="px-3 py-2 rounded-lg bg-primary text-on-primary text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {uploading ? "Uploading…" : "Upload image"}
                    </button>
                    <button
                      onClick={() => set({ workSamples: [...form.workSamples, { type: "Live Website", title: "", url: "" }] })}
                      className="px-3 py-2 rounded-lg border border-outline-variant text-xs font-semibold text-on-surface flex items-center gap-1.5"
                    >
                      <Link2 size={14} /> Add a URL
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPickImage(e.target.files?.[0])} />
                  </div>

                  {form.workSamples.length === 0 ? (
                    <p className="text-sm text-on-surface-variant py-6 text-center">
                      No samples yet. A job with evidence is worth more in a proposal than one without.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {form.workSamples.map((s: WorkSample, i: number) => (
                        <div key={i} className="p-3 rounded-lg border border-outline-variant flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-surface-container grid place-items-center shrink-0 overflow-hidden">
                            {s.type === "Image" && s.url ? (
                              <img src={s.url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <FileText size={16} className="text-on-surface-variant" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex gap-2">
                              <select
                                className={`${input} max-w-[150px]`}
                                value={s.type}
                                onChange={(e) =>
                                  set({ workSamples: form.workSamples.map((x: WorkSample, j: number) => (j === i ? { ...x, type: e.target.value as WorkSampleType } : x)) })
                                }
                              >
                                {SAMPLE_TYPES.map((t) => (
                                  <option key={t}>{t}</option>
                                ))}
                              </select>
                              <input
                                className={input}
                                placeholder="Title"
                                value={s.title}
                                onChange={(e) =>
                                  set({ workSamples: form.workSamples.map((x: WorkSample, j: number) => (j === i ? { ...x, title: e.target.value } : x)) })
                                }
                              />
                            </div>
                            <input
                              className={input}
                              placeholder="https://…"
                              value={s.url}
                              onChange={(e) =>
                                set({ workSamples: form.workSamples.map((x: WorkSample, j: number) => (j === i ? { ...x, url: e.target.value } : x)) })
                              }
                            />
                          </div>
                          <button
                            onClick={() => set({ workSamples: form.workSamples.filter((_: any, j: number) => j !== i) })}
                            className="p-1.5 text-outline hover:text-error shrink-0"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Client conversation ── */}
              {tab === "conversation" && (
                <div className="space-y-4">
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <Info size={15} className="text-primary mt-0.5 shrink-0" />
                    <p className="text-xs text-on-surface leading-relaxed">
                      Paste the conversation you had with this client. It is read once and the useful
                      parts are pulled out — what they asked for, what worried them, and the words they
                      used. <b>Scheduling and invoice chatter is identified and set aside</b>, so a long
                      thread about logistics never makes a job look more relevant than it is.
                    </p>
                  </div>

                  <label className="block">
                    <Label>Conversation</Label>
                    <textarea
                      className={`${input} min-h-[220px] resize-y font-mono text-[12px]`}
                      value={form.conversation.transcript}
                      onChange={(e) => setIn("conversation", { transcript: e.target.value })}
                      placeholder={"Client: Hi, we need our Shopify theme reworked…\nAli: Happy to help — is the checkout part of the scope?"}
                    />
                    <span className="block text-[11px] text-on-surface-variant mt-1">
                      Any format. One message per line, or pasted in blocks.
                    </span>
                  </label>

                  {/* Reading the conversation INTO the form. Separate from
                      "Analyse conversation" below, which extracts matching
                      signals from a record that already exists — this fills in
                      the fields, works before the record is saved, and writes
                      nothing until the BD applies it. */}
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-xs text-on-surface-variant">
                      Fill the form from this conversation. Only what it actually says — the rest stays blank for you.
                    </span>
                    <button
                      onClick={readConversation}
                      disabled={reading || !form.conversation.transcript.trim()}
                      className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {reading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                      {reading ? "Reading…" : "Fill fields from conversation"}
                    </button>
                  </div>

                  {extraction && (
                    <ExtractionReview
                      result={extraction}
                      occupied={fieldOccupied}
                      onApply={applyExtraction}
                      onDismiss={() => setExtraction(null)}
                    />
                  )}

                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-xs text-on-surface-variant">
                      {conversation?.signals?.extractedAt
                        ? `Analysed ${new Date(conversation.signals.extractedAt).toLocaleString()}${conversation.isStale ? " · the conversation has changed since" : ""}`
                        : form.conversation.transcript
                        ? "Not analysed yet — save the record first, then analyse."
                        : ""}
                    </span>
                    <button
                      onClick={async () => {
                        if (!recordId) return;
                        setAnalysing(true);
                        const c = await extractConversation(recordId, true);
                        setAnalysing(false);
                        if (c) setConversation(c);
                      }}
                      disabled={analysing || !recordId || !form.conversation.transcript}
                      title={!recordId ? "Save the record first" : ""}
                      className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {analysing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                      {analysing ? "Reading…" : conversation?.signals?.extractedAt ? "Re-analyse" : "Analyse conversation"}
                    </button>
                  </div>

                  {conversation?.signals?.signalDensity !== null && conversation?.signals?.signalDensity !== undefined && (
                    <>
                      {/* The headline number: how much of this was actually about the work. */}
                      <div
                        className={`p-3 rounded-lg border ${
                          conversation.signals.signalDensity >= 50
                            ? "bg-tertiary-container/40 border-tertiary/30"
                            : "bg-amber-50 border-amber-300/50"
                        }`}
                      >
                        <p className="text-sm font-bold text-on-surface">
                          Signal density {conversation.signals.signalDensity}%
                        </p>
                        <p className="text-xs text-on-surface-variant mt-0.5 leading-relaxed">
                          {conversation.signals.densityReason}
                        </p>
                        {conversation.signals.signalDensity < 30 && (
                          <p className="text-xs font-semibold text-amber-800 mt-1.5">
                            Too little substance to count as matching evidence. It is still kept, and can
                            still help with proposal wording.
                          </p>
                        )}
                      </div>

                      <SignalList title="What they asked" items={conversation.signals.clientQuestions} />
                      <SignalList title="What they needed" items={conversation.signals.requirements} />
                      <SignalList title="What mattered most" items={conversation.signals.priorities} />
                      <SignalList
                        title="Their words for things"
                        items={conversation.signals.terminology}
                        hint="Used when writing the next proposal — their vocabulary, not ours."
                        chips
                      />

                      {conversation.signals.objections.length > 0 && (
                        <div>
                          <Label>Concerns raised</Label>
                          <div className="space-y-2">
                            {conversation.signals.objections.map((o, i) => (
                              <div key={i} className="p-2.5 rounded-lg bg-surface-container-low text-sm">
                                <p className="text-on-surface font-medium">{o.concern}</p>
                                {o.howAddressed && (
                                  <p className="text-on-surface-variant text-xs mt-1">
                                    Answered with: {o.howAddressed}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <SignalList title="Wording that moved it forward" items={conversation.signals.effectiveResponses} />
                      <SignalList title="Buying signals" items={conversation.signals.decisionSignals} />

                      {conversation.signals.logistics.length > 0 && (
                        <details>
                          <summary className="text-[11px] font-semibold text-on-surface-variant cursor-pointer hover:text-on-surface">
                            Logistics set aside ({conversation.signals.logistics.length}) — kept, never used for matching
                          </summary>
                          <ul className="mt-2 space-y-1">
                            {conversation.signals.logistics.map((l, i) => (
                              <li key={i} className="text-xs text-on-surface-variant">· {l}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── AI ── */}
              {tab === "ai" && (
                <div className="space-y-4">
                  {/* Stated once, plainly, rather than implied by styling alone. */}
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-300/50">
                    <Sparkles size={15} className="text-amber-700 mt-0.5 shrink-0" />
                    <p className="text-xs text-on-surface leading-relaxed">
                      Everything on this tab is <b>generated from the record</b>, not entered by anyone.
                      It is read-only, and it is rewritten whenever the record changes.
                    </p>
                  </div>

                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-xs text-on-surface-variant">
                      {ai?.generatedAt
                        ? `Generated ${new Date(ai.generatedAt).toLocaleString()}${ai.isStale ? " · the record has changed since" : ""}`
                        : "Not generated yet"}
                    </span>
                    <button
                      onClick={runAi}
                      disabled={generating}
                      className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {generating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                      {generating ? "Generating…" : ai?.generatedAt ? "Regenerate" : "Generate"}
                    </button>
                  </div>

                  {ai?.highlights && (
                    <div>
                      <Label>Job highlights</Label>
                      <p className="text-sm text-on-surface leading-relaxed p-3 rounded-lg bg-surface-container-low">{ai.highlights}</p>
                    </div>
                  )}

                  {ai?.relevanceSignals && (
                    <div>
                      <Label>Relevance signals</Label>
                      <p className="text-sm text-on-surface leading-relaxed p-3 rounded-lg bg-surface-container-low">{ai.relevanceSignals}</p>
                    </div>
                  )}

                  {ai?.tags && Object.keys(ai.tags).length > 0 && (
                    <div>
                      <Label>Derived tags</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        {ai.tags.jobType && <div className="p-2.5 rounded-lg bg-surface-container-low"><b className="text-on-surface-variant">Job type:</b> {ai.tags.jobType}</div>}
                        {ai.tags.industry && <div className="p-2.5 rounded-lg bg-surface-container-low"><b className="text-on-surface-variant">Industry:</b> {ai.tags.industry}</div>}
                        {ai.tags.experienceSignal && <div className="p-2.5 rounded-lg bg-surface-container-low"><b className="text-on-surface-variant">Scale:</b> {ai.tags.experienceSignal}</div>}
                        {ai.tags.clientSatisfaction && <div className="p-2.5 rounded-lg bg-surface-container-low"><b className="text-on-surface-variant">Client:</b> {ai.tags.clientSatisfaction}</div>}
                      </div>
                      {(ai.tags.skillsDemonstrated?.length ?? 0) > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {ai.tags.skillsDemonstrated!.map((s) => (
                            <span key={s} className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[11px] font-semibold">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Traceability: which stored fields this was built from. */}
                  {(ai?.derivedFrom?.length ?? 0) > 0 && (
                    <div className="pt-2 border-t border-outline-variant">
                      <span className="text-[11px] text-on-surface-variant">
                        Derived from: {ai!.derivedFrom.join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-outline-variant">
          <span className="text-[11px] text-on-surface-variant flex items-center gap-1">
            <Star size={12} /> Records stay with this freelance account only
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-outline-variant text-sm font-semibold text-on-surface">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || loading}
              className="px-4 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold disabled:opacity-50"
            >
              {saving ? "Saving…" : recordId ? "Save changes" : "Add record"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobRecordModal;
